import { EnvironmentInjector, Injectable, Injector } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { from, map, Subject } from 'rxjs';
import { AbstractTableService } from '../components/table/services';
import { downloadBlob, getBinaryFromFile } from '../utils/helper-funcation';
import { ToastrService } from 'ngx-toastr';
import { UploadComponent } from '../components/upload/upload.component';
import { FailedUploadDialogComponent } from '../components/upload/failed-upload-dialog/failed-upload-dialog.component';
import { WrongTemplateDialogComponent } from '../components/upload/wrong-template-dialog/wrong-template-dialog.component';
import { finalize, switchMap } from 'rxjs/operators';

export interface ImportErrorPayload {
  id?: number;
  message?: string;
  wrongTemplate?: { key: string; label: string };
}

/**
 * A globális `ErrorInterceptor` (`core/interceptors/error.interceptor.ts`)
 * minden HTTP hibát egységesen `{statusCode, timestamp, path, message}`
 * alakra csomagol, majd ezt az objektumot dobja tovább. A backend viszont
 * `BadRequestException({id, message})` / `BadRequestException({wrongTemplate,
 * message})` payloaddal dob - ez emiatt a válasz `message` mezője ALATT
 * landol, nem közvetlenül a hibaobjektumon. Ez a helper oldja fel ezt, hogy
 * az `id`/`wrongTemplate` ténylegesen elérhető legyen a `subscribe`
 * `error` ágában (enélkül a "javítási útmutató" letöltés soha nem nyílt
 * meg, mert `error?.id` mindig `undefined` volt).
 */
export function extractImportErrorPayload(error: unknown): ImportErrorPayload {
  const body = error as { message?: unknown } | undefined;
  if (body && typeof body.message === 'object' && body.message !== null) {
    return body.message as ImportErrorPayload;
  }
  if (typeof body?.message === 'string') {
    return { message: body.message };
  }
  return {};
}
export enum UploadEvents {
  UPLOAD = 'upload',
  DOWNLOAD_EXAMPLE = 'downloadExample',
  DOWNLOAD_UPLOAD_ISSUES = 'downloadUploadIssues',
}
export interface UploadCommunicator {
  event: UploadEvents;
  blob?: Blob;
  issueId?: number;
  dialogRef?: DynamicDialogRef;
}

/**
 * `AbstractTableService.TABLE_SETTINGS_KEY` -> letölthető fájlnévhez való
 * előtag. A minta és a javítási útmutató fájlneve korábban mindig
 * "ügyfél.xls" volt, függetlenül attól, melyik táblához tartozott -
 * ez adja a helyes, táblánkénti nevet.
 */
const TABLE_EXPORT_FILE_NAME_PREFIXES: Record<string, string> = {
  'client-list': 'ugyfelek',
  'customer-account-list': 'vevok',
  'product-list': 'termekek',
  'projects-list': 'projektek',
  'worker-list': 'dolgozok',
  'user-list': 'felhasznalok',
  'worker-time-list': 'munkaido',
  'payroll-list': 'berezes',
  'payroll-payment-list': 'elolegkifizetesek',
  'payroll-settlement-list': 'ber_elszamolas',
  'stock-import-list': 'raktari_bevet',
};

@Injectable()
export class UploadService {
  showError: boolean = false;
  uploadDialogRef: DynamicDialogRef | undefined;
  failedUploadDialogRef: DynamicDialogRef;
  /** Amíg igaz, fut az import gRPC hívás - a dialógus ez alatt letiltja a FELTÖLTÉS gombot és jelez. */
  uploading = false;

  constructor(
    protected tableService: AbstractTableService<unknown>,
    protected toastr: ToastrService,
    protected dialogService: DialogService,
  ) {}
  
  openUploadDialog() {
    this.uploadDialogRef = this.dialogService.open(UploadComponent, {
        header: 'Dokumentumok feltöltése',
        width: '600px',
        height: '400px',
        closable: true,
        closeOnEscape: true,
        dismissableMask: true,
        modal: true,
        baseZIndex: 10000,
        style: {
            padding: '1rem'
        }
    });
  }

  openFailedUploadDialog(issueId: number) {
    this.failedUploadDialogRef = this.dialogService.open(
      FailedUploadDialogComponent,
      {
        header: 'Hibás adatok',
        width: '70%',
        data: { issueId },
      }
    );
  }

  openWrongTemplateDialog(wrongTemplate: { key: string; label: string }, blob: Blob) {
    this.dialogService.open(WrongTemplateDialogComponent, {
      header: 'Másik táblához tartozó fájl',
      width: '500px',
      data: { wrongTemplate, blob },
    });
  }

  async importFile(file: File) {
    try {
      const blob = await getBinaryFromFile(file);
      this.uploading = true;
      this.tableService
        .importFile(blob)
        .pipe(finalize(() => (this.uploading = false)))
        .subscribe({
          next: () => {
            this.toastr.success('Sikeres feltöltés!');
            if (this.uploadDialogRef) {
              this.uploadDialogRef.close();
            }
          },
          error: (error: unknown) => {
            // Három eset: (1) a fájl egy MÁSIK tábla sablonjának felel meg -
            // felajánljuk az egykattintásos feltöltést oda; (2) van issueId -
            // konkrét sorok hibásak, letölthető riporttal; (3) sem ez, sem az
            // (pl. teljesen hibás formátum) - csak a hibaüzenetet mutatjuk.
            const payload = extractImportErrorPayload(error);
            if (payload.wrongTemplate) {
              this.openWrongTemplateDialog(payload.wrongTemplate, blob);
            } else if (payload.id) {
              this.openFailedUploadDialog(payload.id);
            } else {
              this.toastr.error(
                payload.message || 'A feltöltött fájl feldolgozása sikertelen.'
              );
            }
            if (this.uploadDialogRef) {
              this.uploadDialogRef.close();
            }
          },
        });
    } catch (error) {
      console.error('Error during file import:', error);
      this.uploading = false;
      this.toastr.error('Hiba történt a feltöltés során');
    }
  }

  /** A `TABLE_SETTINGS_KEY`-hez tartozó fájlnév-előtag, ismeretlen kulcsra generikus alapértelmezéssel. */
  private getTableExportFileNamePrefix(): string {
    return TABLE_EXPORT_FILE_NAME_PREFIXES[this.tableService.TABLE_SETTINGS_KEY] ?? 'adatok';
  }

  downloadExample() {
    this.tableService
      .exportExampleFile()
      .pipe(
        map((res) => {
          return new Blob([res as ArrayBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
        }),
        switchMap((data) =>
          from(downloadBlob(data, `${this.getTableExportFileNamePrefix()}_sablon.xlsx`))
        )
      )
      .subscribe(() => {
        this.toastr.success('Sikeresen letöltve!');
      });
  }

  downloadUploadIssues(issueId: number, dialogRef: DynamicDialogRef) {
    this.tableService
      .exportFailedUpload(issueId)
      .pipe(
        map((res) => {
          return new Blob([res as ArrayBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          });
        }),
        switchMap((data) =>
          from(
            downloadBlob(
              data,
              `${this.getTableExportFileNamePrefix()}_javitasi_utmutato.xlsx`
            )
          )
        )
      )
      .subscribe(() => {
        this.toastr.success('Sikeres letöltés!');
        dialogRef?.close();
      });
  }
}
